// Geometry helpers — turn the v3 coordinate/canopy data into SVG path strings
// for Konva.Path. Supports straight + curve (quadratic) + bezier (cubic)
// segments and the ["closing", ...] trailing entry, so curved shapes render
// correctly (not just a straight approximation).

// coordinates entries:
//   [x, y]                                  start / straight segment end
//   [x, y, "curve",  cpX, cpY]              quadratic to (x,y)
//   [x, y, "bezier", cp1X, cp1Y, cp2X, cp2Y] cubic to (x,y)
//   ["closing", "curve",  cpX, cpY]         curved close back to start
//   ["closing", "bezier", c1X, c1Y, c2X, c2Y] cubic close back to start
export function coordsToPathData(coordinates = [], closed = false) {
  if (!coordinates.length) return '';
  const start = coordinates.find((c) => Array.isArray(c) && typeof c[0] === 'number');
  if (!start) return '';
  let d = `M ${start[0]} ${start[1]}`;
  let hasExplicitClose = false;

  for (let i = 1; i < coordinates.length; i += 1) {
    const c = coordinates[i];
    if (!Array.isArray(c)) continue;
    if (c[0] === 'closing') {
      // close back to the start point with a curve
      if (c[1] === 'curve') d += ` Q ${c[2]} ${c[3]} ${start[0]} ${start[1]}`;
      else if (c[1] === 'bezier') d += ` C ${c[2]} ${c[3]} ${c[4]} ${c[5]} ${start[0]} ${start[1]}`;
      hasExplicitClose = true;
      continue;
    }
    const ex = c[0]; const ey = c[1];
    if (c[2] === 'curve') d += ` Q ${c[3]} ${c[4]} ${ex} ${ey}`;
    else if (c[2] === 'bezier') d += ` C ${c[3]} ${c[4]} ${c[5]} ${c[6]} ${ex} ${ey}`;
    else d += ` L ${ex} ${ey}`;
  }

  if (closed && !hasExplicitClose) d += ' Z';
  else if (closed) d += ' Z';
  return d;
}

// --- canopy outlines (lobed / conifer) -------------------------------------
// Ported faithfully from v1's canopy styles, using p5-compatible seeded Perlin
// noise + random, so the same canopySeed yields the same shape as the old
// planner. `diameter` is the ellipse width in plan units.
import P5Noise from './p5noise';

// Lobed: a subtly wavy circle (Perlin noise, amplitude 0.11), rendered as a
// closed spline. Returns flat [x,y,...] points centred at 0,0.
export function lobedPoints(diameter, seed = 1) {
  const noise = new P5Noise();
  noise.noiseSeed(seed); noise.randomSeed(seed);
  const r = diameter / 2;
  const numPoints = Math.max(12, Math.round(diameter));
  const noiseScale = 3; const amplitude = 0.11;
  const pts = [];
  for (let i = 0; i < numPoints; i += 1) {
    const angle = (i / numPoints) * Math.PI * 2;
    const nx = Math.cos(angle) * noiseScale + noiseScale;
    const ny = Math.sin(angle) * noiseScale + noiseScale;
    const nv = noise.noise(nx, ny);
    const radius = r * (1 + amplitude * (nv * 2 - 1));
    pts.push(radius * Math.cos(angle), radius * Math.sin(angle));
  }
  return pts;
}

// Conifer: a filled circle behind many short radiating needle lines. Returns
// { radius, needles: [{ix,iy,ox,oy}, ...] } centred at 0,0.
export function coniferNeedles(diameter, seed = 1) {
  const noise = new P5Noise();
  noise.noiseSeed(seed); noise.randomSeed(seed);
  const r = diameter / 2;
  const numNeedles = Math.max(6, Math.pow(diameter, 0.6) * 1.2);
  const needles = [];
  for (let i = 0; i < numNeedles; i += 1) {
    const baseAngle = (i / numNeedles) * Math.PI * 2;
    const angleNoise = noise.noise((i * 3.7) / numNeedles + 10);
    const angle = baseAngle + (angleNoise - 0.5) * 4.5;
    const outerNoise = noise.noise((i * 5.0) / numNeedles);
    const outerR = r + Math.pow(r, 0.8) * (0.01 + outerNoise * 0.09);
    const innerR = r * (0.15 + noise.random() * 0.75);
    needles.push({
      ix: innerR * Math.cos(angle), iy: innerR * Math.sin(angle),
      ox: outerR * Math.cos(angle), oy: outerR * Math.sin(angle),
    });
  }
  return { radius: r, needles };
}

export function hasCurves(coordinates = []) {
  return coordinates.some((c) => Array.isArray(c) && (c[2] === 'curve' || c[2] === 'bezier' || c[0] === 'closing'));
}
